import { CaretDown, Check, FileText, Plus, X } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { type KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DocumentTask } from "~/api/opsTypes";
import {
  useCompleteDocumentTask,
  useDeleteDocumentTask,
  useDocumentTasks,
  useMembers,
  useUpdateDocumentTask,
} from "~/api/queries";
import { IconButton } from "~/foundation/components/IconButton";
import { cn } from "~/foundation/utils/cn";

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** ISO → "YYYY-MM-DDTHH:mm" in local time, for a `datetime-local` input. */
function toLocalInput(iso: string | null): string {
  if (!iso) {
    return "";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function RelatedInput({ onAdd, onCancel }: { onAdd: (id: string) => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      const match = value.match(UUID_RE)?.[0];
      if (match) {
        onAdd(match);
      }
      onCancel();
    } else if (event.key === "Escape") {
      onCancel();
    }
  };
  return (
    <input
      ref={inputRef}
      value={value}
      onChange={(event) => setValue(event.target.value)}
      onKeyDown={handleKeyDown}
      onBlur={onCancel}
      placeholder={t("Paste a document id…")}
      className="h-6 w-44 rounded-sm border border-input-border-focus bg-bg px-2 text-small text-text focus:outline-none"
    />
  );
}

function TaskRow({ task }: { task: DocumentTask }) {
  const { t } = useTranslation();
  const { data: members } = useMembers();
  const complete = useCompleteDocumentTask();
  const update = useUpdateDocumentTask();
  const remove = useDeleteDocumentTask();
  const [addingRelated, setAddingRelated] = useState(false);

  const isDone = task.status === "done";
  const overdue = !isDone && task.dueDate !== null && new Date(task.dueDate).getTime() < Date.now();
  const activeMembers = (members ?? []).filter((member) => !member.suspended);

  return (
    <li className="flex items-start gap-2 rounded-md px-2.5 py-2 hover:bg-bg">
      <input
        type="checkbox"
        checked={isDone}
        onChange={() => complete.mutate({ id: task.id, done: !isDone })}
        aria-label={t("Mark done")}
        className="mt-1 shrink-0 cursor-pointer accent-accent"
      />
      <div className="min-w-0 flex-1">
        <p className={cn("text-body text-text", isDone && "line-through")}>{task.title}</p>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <select
            value={task.assigneeId ?? ""}
            onChange={(event) =>
              update.mutate({
                id: task.id,
                patch: { hasAssignee: true, assigneeId: event.target.value || null },
              })
            }
            title={t("Assignee")}
            className="h-6 max-w-40 rounded-sm border border-input-border bg-bg px-1.5 text-small text-text"
          >
            <option value="">{t("Unassigned")}</option>
            {activeMembers.map((member) => (
              <option key={member.id} value={member.id}>
                {member.name}
              </option>
            ))}
          </select>
          <input
            type="datetime-local"
            value={toLocalInput(task.dueDate)}
            onChange={(event) =>
              update.mutate({
                id: task.id,
                patch: {
                  hasDueDate: true,
                  dueDate: event.target.value ? new Date(event.target.value).toISOString() : null,
                },
              })
            }
            title={t("Due date")}
            className={cn(
              "h-6 rounded-sm border bg-bg px-1.5 text-small",
              overdue ? "border-danger text-danger" : "border-input-border text-text",
            )}
          />
          {task.relatedDocumentIds.map((relatedId) => (
            <Link
              key={relatedId}
              to="/documents/$documentId"
              params={{ documentId: relatedId }}
              title={relatedId}
              className="inline-flex h-6 max-w-40 items-center gap-1 truncate rounded-full bg-bg-tertiary px-2 text-small text-text-secondary hover:text-accent"
            >
              <FileText size={13} aria-hidden />
              {relatedId}
            </Link>
          ))}
          {addingRelated ? (
            <RelatedInput
              onAdd={(id) =>
                update.mutate({
                  id: task.id,
                  patch: { relatedDocumentIds: [...task.relatedDocumentIds, id] },
                })
              }
              onCancel={() => setAddingRelated(false)}
            />
          ) : (
            <button
              type="button"
              onClick={() => setAddingRelated(true)}
              title={t("Link another document")}
              className="inline-flex h-6 items-center gap-1 rounded-full border border-dashed border-input-border px-2 text-small text-text-tertiary hover:border-accent hover:text-accent"
            >
              <Plus size={11} aria-hidden />
              {t("doc")}
            </button>
          )}
        </div>
        {task.sourceQuote ? (
          <p className="mt-1 text-small text-text-tertiary italic">“{task.sourceQuote}”</p>
        ) : null}
      </div>
      <IconButton
        icon={X}
        size="sm"
        aria-label={t("Delete task")}
        onClick={() => remove.mutate(task.id)}
      />
    </li>
  );
}

/**
 * The document's Tasks panel: a durable, managed list of tasks that came out of this document
 * (Taskize → DocumentTask). Renders nothing at all when the list is empty — kms's own rule,
 * kept here: a document with no tasks shows no panel rather than an empty one.
 */
export function DocumentTasksPanel({ documentId }: { documentId: string }) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(true);
  const tasksId = useId();
  const { data: tasks } = useDocumentTasks({ documentId });

  if (!tasks || tasks.length === 0) {
    return null;
  }

  const openCount = tasks.filter((task) => task.status !== "done").length;

  return (
    <div className="my-2 overflow-hidden rounded-md border border-divider bg-bg-secondary">
      <button
        type="button"
        onClick={() => setCollapsed((value) => !value)}
        aria-expanded={!collapsed}
        aria-controls={tasksId}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-start text-body text-text"
      >
        <CaretDown
          size={16}
          aria-hidden
          className={cn(
            "shrink-0 text-text-tertiary transition-transform duration-100",
            collapsed && "-rotate-90 rtl:rotate-90",
          )}
        />
        <Check size={16} aria-hidden className="shrink-0 text-text-tertiary" />
        <strong className="font-semibold">{t("Tasks from this document")}</strong>
        <span className="ms-auto text-small text-text-secondary">
          {t("{{count}} open", { count: openCount })}
        </span>
      </button>

      {collapsed ? null : (
        <ul id={tasksId} className="flex flex-col gap-0.5 p-1">
          {tasks.map((task) => (
            <TaskRow key={task.id} task={task} />
          ))}
        </ul>
      )}
    </div>
  );
}
